import {
  HttpAgent,
  type AgentSubscriber,
  type Context,
  type HttpAgentFetchFn,
  type RunAgentInput,
} from "@ag-ui/client";
import { getConfig } from "./config";
import { csrfToken } from "@/auth/csrf";

// The AG-UI transport for a turn streamed from agent-service through a BFF passthrough
// (@external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): the official @ag-ui/client HttpAgent on the cookie
// session, and the idle watchdog that ends a stalled turn. What a turn carries belongs to its caller; for the
// Assistant drawer that is agent-agui.ts.
//
// The watchdog relies on the route behind the passthrough sending a heartbeat (agent-service writes an SSE comment
// every 10s) and on the BFF forwarding bytes unbuffered. A route without either has a long turn ended by the client
// while the server is still working on it.

// Cookie-session auth + per-request CSRF header (read at request time, since the token can rotate),
// mirroring the legacy SSE fetch options.
class SparkAgent extends HttpAgent {
  protected override requestInit(input: RunAgentInput): RequestInit {
    const init = super.requestInit(input);
    const csrf = csrfToken();
    return {
      ...init,
      credentials: "include",
      headers: {
        ...(init.headers as Record<string, string>),
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
    };
  }
}

/**
 * Per-agent "upstream bytes arrived" notifier, installed for the duration of one turn.
 *
 * The idle watchdog below has to reset on BYTES, not on parsed AG-UI events: agent-service proves
 * liveness with an SSE heartbeat comment every 10s, and a comment carries no event, so an
 * event-only watchdog is blind to a healthy-but-quiet stream. The BFF passthrough and the legacy
 * SSE reader both already count bytes; this makes the AG-UI path agree with them. Measured
 * 2026-09-09: a 44.4s model call plus a 0.6s suggestion call left a 45.05s event gap against a
 * 45s window, and the turn was killed ~50ms before its first token. Server-side it had completed.
 */
const byteTap = new WeakMap<object, () => void>();

/**
 * `fetch` for the AG-UI transport, passing every chunk through untouched and reporting that it
 * arrived. A `TransformStream` rather than a tee, so nothing is buffered and backpressure is the
 * consumer's as before.
 */
function tappedFetch(agent: object): HttpAgentFetchFn {
  return async (url, requestInit) => {
    const response = await fetch(url, requestInit);
    if (!response.body) return response;
    return new Response(
      response.body.pipeThrough(
        new TransformStream<Uint8Array, Uint8Array>({
          transform(chunk, controller) {
            byteTap.get(agent)?.();
            controller.enqueue(chunk);
          },
        }),
      ),
      response,
    );
  };
}

// `.invalid` never resolves. A URL that keeps this origin when resolved against it keeps the page's origin too.
const PROBE_ORIGIN = "http://spark.invalid";

// Segments of letters, digits, `_` and `-` only. The origin check below cannot see a traversal that stays under the
// base path (`ai/roleplay/games/../x` resolves to `ai/roleplay/x`), and a game id is interpolated into these paths, so
// a `.` or `..` segment, `%`, a backslash or an empty segment is refused before any URL is built from it.
const PASSTHROUGH_PATH = /^[A-Za-z0-9_-]+(\/[A-Za-z0-9_-]+)*$/;

// The session cookie and the CSRF token ride every request, so the URL must stay on this origin, under the base path.
function passthroughUrl(path: string): string {
  const { basePath } = getConfig();
  const url = `${basePath}${path}`;
  const target = new URL(url, PROBE_ORIGIN);
  if (
    !PASSTHROUGH_PATH.test(path) ||
    target.origin !== PROBE_ORIGIN ||
    !target.pathname.startsWith(new URL(basePath, PROBE_ORIGIN).pathname)
  ) {
    throw new Error(`AG-UI path must stay under the base path: ${JSON.stringify(path)}`);
  }
  return url;
}

/**
 * A cookie-session agent for the BFF passthrough at `path`, relative to the base path (`ai/agui`). The response bytes
 * of every turn run on it feed that turn's watchdog.
 */
export function createSparkAgent(path: string): HttpAgent {
  // `agent` is read only when the fetch actually runs, which is after the binding is initialised.
  const agent: HttpAgent = new SparkAgent({
    url: passthroughUrl(path),
    fetch: (url, requestInit) => tappedFetch(agent)(url, requestInit),
  });
  return agent;
}

// Idle watchdog window for a turn (spark#228 C3). A turn can legitimately pause between events (a slow tool call
// leaves a real gap between TOOL_CALL_END and TOOL_CALL_RESULT), so this must be generous enough to catch a wedge,
// not clip a slow turn.
export const DEFAULT_TURN_IDLE_MS = 45_000;

export interface WatchedTurn {
  // The run id the caller minted for this attempt, so it can find the run again after a stall (the roleplay game's
  // log returns it). Without one HttpAgent mints its own.
  runId?: string;
  context: Context[];
  forwardedProps: Record<string, unknown>;
  abortController: AbortController;
  // Idle window before a stalled turn is aborted. Defaults to DEFAULT_TURN_IDLE_MS.
  idleMs?: number;
  // Reports a stalled turn, once.
  onStall: () => void;
}

/**
 * Runs one turn on `agent` under the idle watchdog, handing every event to `subscriber`. Resolves when the run ends
 * (RUN_FINISHED, RUN_ERROR, a stall or the caller's abort) and rejects on a transport failure.
 */
export async function runWatchedTurn(
  agent: HttpAgent,
  turn: WatchedTurn,
  subscriber: AgentSubscriber,
): Promise<void> {
  // After idleMs of complete silence, report the stall and abort the transport, so a mid-stream stall cannot hang
  // forever. The report cannot be left to the abort: @ag-ui/client's HttpAgent SWALLOWS an AbortError (it neither
  // rejects runAgent nor emits onRunErrorEvent), so a bare abort would let the caller treat the stall as a normal end.
  const idleMs = turn.idleMs ?? DEFAULT_TURN_IDLE_MS;
  let idle: ReturnType<typeof setTimeout> | undefined;
  let stalled = false;
  const clearIdle = () => {
    if (idle) clearTimeout(idle);
    idle = undefined;
  };
  const armIdle = () => {
    if (stalled) return;
    clearIdle();
    idle = setTimeout(() => {
      stalled = true;
      turn.onStall();
      turn.abortController.abort();
    }, idleMs);
  };

  const watched: AgentSubscriber = {
    ...subscriber,
    onEvent: (params) => {
      armIdle();
      return subscriber.onEvent?.(params);
    },
    // The stall already reported this turn, so a RUN_ERROR after it must not report it again.
    onRunErrorEvent: (params) => (stalled ? undefined : subscriber.onRunErrorEvent?.(params)),
  };

  armIdle();
  // Bytes are the liveness signal; events stay wired too, so the watchdog rearms on whichever
  // arrives first.
  byteTap.set(agent, armIdle);
  try {
    await agent.runAgent(
      {
        ...(turn.runId === undefined ? {} : { runId: turn.runId }),
        context: turn.context,
        forwardedProps: turn.forwardedProps,
        abortController: turn.abortController,
      },
      watched,
    );
  } finally {
    byteTap.delete(agent);
    clearIdle();
  }
}
